import { Grid, Stack } from "@mui/material";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { UseTransientConfigurationReturn } from "../hooks/useTransientConfiguration";
import type { TransientConfigurationFormState } from "../model/transientConfiguration";
import type { ScenarioDefinitionFormState } from "../model/transientScenario";

import {
  findRecalculationMethod,
  findRoomFunctionVariant,
  RECALCULATION_METHODS,
  ROOM_FUNCTION_VARIANT_VALUES,
  ROOM_FUNCTION_VARIANTS,
} from "../model/transientConfiguration";
import { ConfigurationSwitchRow } from "./ConfigurationSwitchRow";
import { TransientDropdownFieldRow } from "./TransientDropdownFieldRow";
import { TransientMultiSelectFieldRow } from "./TransientMultiSelectFieldRow";
import { TransientNumericFieldRow } from "./TransientNumericFieldRow";

type BooleanConfigurationKey = {
  [K in keyof TransientConfigurationFormState]: TransientConfigurationFormState[K] extends boolean ? K : never;
}[keyof TransientConfigurationFormState];

type ConfigurationColumnField =
  | { key: BooleanConfigurationKey; kind: "switch"; labelKey: string }
  | { key: NumericConfigurationKey; kind: "numeric"; labelKey: string; step?: string }
  | { kind: "recalculation" }
  | { kind: "roomFunction" }
  | { kind: "scenarios" };

type NumericConfigurationKey = {
  [K in keyof TransientConfigurationFormState]: TransientConfigurationFormState[K] extends number ? K : never;
}[keyof TransientConfigurationFormState];

const CONFIGURATION_COLUMNS: ConfigurationColumnField[][] = [
  [
    { kind: "scenarios" },
    { key: "time_interval_in_min", kind: "numeric", labelKey: "timeIntervalInMin" },
    { kind: "recalculation" },
    { key: "t_air_exhaust", kind: "numeric", labelKey: "tAirExhaust" },
  ],
  [
    { key: "delta_time_i", kind: "numeric", labelKey: "deltaTimeI" },
    { kind: "roomFunction" },
    { key: "percentile_to_consider", kind: "numeric", labelKey: "percentileToConsider" },
    { key: "dynamic_hvac_start_point", kind: "switch", labelKey: "dynamicHvacStartPoint" },
  ],
];

type TransientConfigurationFieldsProps = {
  configuration: UseTransientConfigurationReturn;
  scenarios: ScenarioDefinitionFormState[];
};

export const TransientConfigurationFields = ({ configuration, scenarios }: TransientConfigurationFieldsProps) => {
  const { t } = useTranslation();
  const { configuration: values, updateConfiguration } = configuration;
  const recalculationLabels = useMemo(
    () => RECALCULATION_METHODS.map((method) => t(`Execution.transient.configuration.recalculationMethod.${method}`)),
    [t]
  );
  const roomFunctionLabels = useMemo(
    () =>
      ROOM_FUNCTION_VARIANTS.map((variant) => t(`Execution.transient.configuration.roomFunctionVariant.${variant}`)),
    [t]
  );
  const selectedRoomFunctionVariant =
    ROOM_FUNCTION_VARIANTS.find((variant) => ROOM_FUNCTION_VARIANT_VALUES[variant] === values.room_function_variant) ??
    "template_function";

  const scenarioOptions = useMemo(() => scenarios.map((scenario) => String(scenario.id)), [scenarios]);
  const scenarioLabels = useMemo(() => scenarios.map((scenario) => scenario.name), [scenarios]);

  const renderField = (field: ConfigurationColumnField): ReactNode => {
    if (field.kind === "numeric") {
      return (
        <TransientNumericFieldRow
          alignControlEnd
          key={field.key}
          label={t(`Execution.transient.configuration.${field.labelKey}`)}
          onChange={(value) => updateConfiguration(field.key, value)}
          step={field.step}
          value={values[field.key]}
        />
      );
    }
    if (field.kind === "switch") {
      return (
        <ConfigurationSwitchRow
          alignControl
          checked={values[field.key]}
          key={field.key}
          label={t(`Execution.transient.configuration.${field.labelKey}`)}
          onChange={(value) => updateConfiguration(field.key, value)}
        />
      );
    }
    if (field.kind === "scenarios") {
      return (
        <TransientMultiSelectFieldRow
          alignControlEnd
          displayOptions={scenarioLabels}
          key="scenarios"
          label={t("Execution.transient.configuration.scenariosToRun")}
          onChange={(selected) =>
            updateConfiguration(
              "scenarios",
              selected.map(Number).filter((id) => Number.isFinite(id))
            )
          }
          options={scenarioOptions}
          selectedValues={values.scenarios.map(String)}
        />
      );
    }
    if (field.kind === "recalculation") {
      return (
        <TransientDropdownFieldRow
          alignControlEnd
          displayOptions={recalculationLabels}
          key="recalculation"
          label={t("Execution.transient.configuration.recalculationMethod.label")}
          onSelect={(value) => {
            const method = findRecalculationMethod(value);
            if (method) updateConfiguration("recalculation_method", method);
          }}
          options={RECALCULATION_METHODS}
          selectedValue={values.recalculation_method}
        />
      );
    }
    return (
      <TransientDropdownFieldRow
        alignControlEnd
        displayOptions={roomFunctionLabels}
        key="roomFunction"
        label={t("Execution.transient.configuration.roomFunctionVariant.label")}
        onSelect={(value) => {
          const variant = findRoomFunctionVariant(value);
          if (variant) updateConfiguration("room_function_variant", ROOM_FUNCTION_VARIANT_VALUES[variant]);
        }}
        options={ROOM_FUNCTION_VARIANTS}
        selectedValue={selectedRoomFunctionVariant}
      />
    );
  };

  return (
    <Grid className="w-full" columnSpacing={12} container>
      {CONFIGURATION_COLUMNS.map((fields, columnIndex) => (
        <Grid item key={columnIndex} xs={6}>
          <Stack className="w-full flex-col gap-4">{fields.map(renderField)}</Stack>
        </Grid>
      ))}
    </Grid>
  );
};
